import { useState, useCallback, useRef, MutableRefObject } from 'react';
import { pdfjs, Document, Page } from 'react-pdf';
import { useDraggable } from 'react-use-draggable-scroll';
import round from 'lodash/round';

import { makeStyles } from '@mui/styles';

import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';

import AssignmentLateOutlinedIcon from '@mui/icons-material/AssignmentLateOutlined';
import ZoomOutOutlinedIcon from '@mui/icons-material/ZoomOutOutlined';
import ZoomInOutlinedIcon from '@mui/icons-material/ZoomInOutlined';
import RotateLeftOutlinedIcon from '@mui/icons-material/RotateLeftOutlined';
import RotateRightOutlinedIcon from '@mui/icons-material/RotateRightOutlined';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import ArrowForwardOutlinedIcon from '@mui/icons-material/ArrowForwardOutlined';
import RestartAltOutlinedIcon from '@mui/icons-material/RestartAltOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  // @ts-ignore
  import.meta.url,
).toString();

const minScale = 0.05;
const zoomParam = 0.8;

function zoomStep(scale: number, zoomIn = true) {
  const step = zoomIn ? scale >= zoomParam : scale > zoomParam;
  return step ? 0.1 : 0.05;
}

const toolbarSx = {
  background:
    'linear-gradient(0deg, #212121 -15%, rgba(163, 163, 163, 0) 100%)',
};

interface PdfViewerProps {
  url: string;
  onDownload?: () => void;
}

export default function PdfViewer({ url, onDownload }: PdfViewerProps) {
  const classes = useStyles();

  const [page, setPage] = useState(1);
  const [scale, setScale] = useState(1);
  const [rotate, setRotate] = useState(0);
  const [isLoaded, setLoaded] = useState(false);
  const [totalPages, setTotalPages] = useState(0);

  const isDragScroll = isLoaded;

  const ref = useRef<MutableRefObject<HTMLElement>>();
  // @ts-ignore
  const { events } = useDraggable(ref);

  const handlePrevPage = useCallback(
    () => setPage((prevPage) => prevPage - 1),
    [],
  );

  const handleZoomIn = useCallback(
    () => setScale((prevScale) => round(prevScale + zoomStep(prevScale), 2)),
    [],
  );

  const handleZoomOut = useCallback(
    () =>
      setScale((prevScale) => round(prevScale - zoomStep(prevScale, false), 2)),
    [],
  );

  const handleRotateLeft = useCallback(
    () => setRotate((prevRotate) => (prevRotate || 360) - 90),
    [],
  );

  const handleRotateRight = useCallback(
    () => setRotate((prevRotate) => (prevRotate || 360) + 90),
    [],
  );

  const handleNextPage = useCallback(
    () => setPage((prevPage) => prevPage + 1),
    [],
  );

  const handleReset = useCallback(() => {
    setRotate(0);
    setScale(1);
    setPage(1);
  }, []);

  const handleLoadError = useCallback(() => setLoaded(false), []);

  const handleLoadSuccess = useCallback(
    ({ numPages }: { numPages: number }) => {
      setTotalPages(numPages);
      setLoaded(true);
    },
    [],
  );

  return (
    <>
      <Box
        width="100%"
        height="100%"
        overflow="hidden"
        position="relative"
        sx={{
          cursor: isDragScroll ? 'move' : 'default',
        }}
        ref={ref}
        {...events}
      >
        <Document
          className={classes.document}
          onLoadSuccess={handleLoadSuccess}
          onLoadError={handleLoadError}
          error={
            <Stack
              width="100%"
              height="100%"
              justifyContent="center"
              alignItems="center"
            >
              <AssignmentLateOutlinedIcon fontSize="large" />
            </Stack>
          }
          file={url}
        >
          <Page
            renderTextLayer={false}
            pageNumber={page}
            scale={scale}
            rotate={rotate}
          />
        </Document>
      </Box>
      {isLoaded && (
        <Box bottom={0} left={0} width="100%" position="absolute">
          <Box
            bottom={0}
            width="100%"
            position="absolute"
            textAlign="center"
            sx={toolbarSx}
          >
            {totalPages > 1 && page > 1 && (
              <IconButton
                color="secondary"
                disabled={page <= 1}
                className={classes.toolbarButton}
                onClick={handlePrevPage}
              >
                <ArrowBackOutlinedIcon />
              </IconButton>
            )}
            {totalPages > 1 && page < totalPages && (
              <IconButton
                color="secondary"
                disabled={page >= totalPages}
                className={classes.toolbarButton}
                onClick={handleNextPage}
              >
                <ArrowForwardOutlinedIcon />
              </IconButton>
            )}
            <IconButton
              color="secondary"
              onClick={handleRotateLeft}
              className={classes.toolbarButton}
            >
              <RotateLeftOutlinedIcon />
            </IconButton>
            <IconButton
              color="secondary"
              onClick={handleRotateRight}
              className={classes.toolbarButton}
            >
              <RotateRightOutlinedIcon />
            </IconButton>

            <IconButton
              color="secondary"
              onClick={handleZoomOut}
              className={classes.toolbarButton}
              disabled={scale <= minScale}
            >
              <ZoomOutOutlinedIcon />
            </IconButton>
            <IconButton
              color="secondary"
              onClick={handleZoomIn}
              className={classes.toolbarButton}
            >
              <ZoomInOutlinedIcon />
            </IconButton>
            <IconButton
              color="secondary"
              onClick={handleReset}
              className={classes.toolbarButton}
            >
              <RestartAltOutlinedIcon />
            </IconButton>
            {onDownload && (
              <IconButton
                color="secondary"
                onClick={onDownload}
                className={classes.toolbarButton}
              >
                <FileDownloadOutlinedIcon />
              </IconButton>
            )}
          </Box>
        </Box>
      )}
    </>
  );
}

const useStyles = makeStyles((theme) => ({
  document: {
    '& .react-pdf__message': {
      width: '100%',
      height: '100%',
      display: 'flex',
      cursor: 'default',
      alignItems: 'center',
      justifyContent: 'center',
    },
  },
  toolbarButton: {
    margin: theme.spacing(0, 0.5),
    height: theme.spacing(6),
    width: theme.spacing(6),

    '& svg': {
      color: 'white',
    },
  },
}));
